/**
 * FAQ copy for the industry pages that keep it as data rather than JSX.
 *
 * Lifted out of each page's local COPY const so the rendered accordion and the
 * FAQPage schema read the same array. See pricing-faqs.ts for the same pattern
 * and the reason for it.
 */

export interface IndustryFaq {
  q: string;
  a: string;
}

export const INDUSTRY_FAQS: Record<
  string,
  Record<'en' | 'ar', IndustryFaq[]>
> = {
  healthcare: {
    en: [
      {
        q: 'Are you HIPAA compliant?',
        a: "We're building toward HIPAA compliance. Our infrastructure includes encryption at rest and in transit, automatic PHI detection, complete audit logs, and BAA-ready contracts. We work with healthcare organizations to meet their specific security requirements. Full HIPAA certification is on our roadmap for 2025.",
      },
      {
        q: 'How do you handle PHI (Protected Health Information)?',
        a: 'All PHI is encrypted (AES-256 at rest, TLS 1.3 in transit). Agent 5 automatically detects and can redact or mask sensitive health information. Complete audit trails track who accessed what and when. Your data is isolated and never used to train our models.',
      },
      {
        q: 'Will the AI give medical advice or diagnoses?',
        a: 'No. Agent 5 handles administrative tasks—scheduling, insurance, billing, refills. It does not diagnose, recommend treatments, or provide medical advice. Medical questions are immediately escalated to your clinical staff with full context.',
      },
      {
        q: 'Can you integrate with our EHR/PMS system?',
        a: 'Yes. We integrate with major systems including Epic, Cerner, Athenahealth, and others through secure APIs. Agent 5 can read appointment slots, verify insurance, and update records based on your permissions and workflows.',
      },
      {
        q: 'What about emergency situations?',
        a: 'Agent 5 is configured to recognize urgent keywords (chest pain, difficulty breathing, etc.) and immediately provides emergency contact information while alerting your on-call team. It never delays urgent care.',
      },
      {
        q: 'How long does setup take for a healthcare organization?',
        a: 'Most healthcare providers are live within 3-4 weeks. This includes security review, EHR integration, knowledge base setup, clinical team training, and phased rollout. We work with your IT and compliance requirements.',
      },
      {
        q: 'Can patients opt out of AI support?',
        a: 'Yes, always. Patients can request a human agent at any time. The handoff is seamless—your team gets full conversation context. We believe AI should help, not force itself on patients who prefer human interaction.',
      },
      {
        q: 'What languages do you support for diverse patient populations?',
        a: 'Agent 5 supports 100+ languages with the same accuracy and security. Automatic language detection means patients can communicate in their preferred language without requesting a translator or waiting.',
      },
    ],
    ar: [
      {
        q: 'هل أنتم متوافقون مع HIPAA؟',
        a: 'نعمل على التوافق مع HIPAA. تشمل بنيتنا التحتية تشفيراً أثناء التخزين والنقل، واكتشافاً تلقائياً للمعلومات الصحية المحمية (PHI)، وسجلّات تدقيق كاملة، وعقوداً جاهزة لاتفاقيات BAA. ونتعاون مع المؤسسات الصحية على تلبية متطلبات الأمان الخاصة بكل منها. وشهادة HIPAA الكاملة مدرجة على خارطة طريقنا لعام ٢٠٢٥.',
      },
      {
        q: 'كيف تتعاملون مع PHI (المعلومات الصحية المحمية)؟',
        a: 'جميع المعلومات الصحية المحمية مشفّرة (AES-256 أثناء التخزين، وTLS 1.3 أثناء النقل). ويكشف النظام المعلومات الصحية الحساسة تلقائياً، مع إمكانية تنقيحها أو حجبها. وتوثّق سجلّات التدقيق الكاملة من وصل إلى ماذا ومتى. بياناتك معزولة، ولا تُستخدم لتدريب نماذجنا إطلاقاً.',
      },
      {
        q: 'هل يقدّم الذكاء الاصطناعي نصائح طبية أو تشخيصات؟',
        a: 'لا. يتولّى النظام المهام الإدارية فقط: الجدولة، والتأمين، والفواتير، وتجديد الوصفات. لا يُشخّص، ولا يوصي بعلاج، ولا يقدّم نصيحة طبية. وتُصعَّد الأسئلة الطبية فوراً لطاقمك السريري بسياق كامل.',
      },
      {
        q: 'هل تتكاملون مع نظام EHR/PMS عندنا؟',
        a: 'نعم. نتكامل مع الأنظمة الكبرى: Epic وCerner وAthenahealth وغيرها، عبر واجهات برمجية آمنة. ويقدر النظام يقرأ المواعيد المتاحة، ويتحقق من التأمين، ويحدّث السجلات وفق الصلاحيات وسير العمل اللي تحدّده.',
      },
      {
        q: 'وش عن حالات الطوارئ؟',
        a: 'النظام مضبوط على التعرّف على الكلمات العاجلة (ألم في الصدر، صعوبة في التنفس، وغيرها)، فيقدّم معلومات الاتصال بالطوارئ مباشرة، وينبّه فريقك المناوب. ولا يؤخّر الرعاية العاجلة بأي حال.',
      },
      {
        q: 'كم يستغرق الإعداد لمؤسسة رعاية صحية؟',
        a: 'تنطلق أغلب مؤسسات الرعاية الصحية خلال ٣-٤ أسابيع. وتشمل هذه المدة المراجعة الأمنية، والتكامل مع نظام EHR، وإعداد قاعدة المعرفة، وتدريب الفريق السريري، والنشر التدريجي. ونعمل وفق متطلبات تقنية المعلومات والامتثال لديك.',
      },
      {
        q: 'يقدر المريض يستغني عن الدعم بالذكاء الاصطناعي؟',
        a: 'نعم، في أي وقت. يقدر المريض يطلب موظف بشري في أي لحظة، والتحويل سلس: يستلم فريقك سياق المحادثة كاملاً. ونرى أن الذكاء الاصطناعي وُجد ليساعد، لا ليُفرض على اللي يفضّل التعامل مع إنسان.',
      },
      {
        q: 'وش اللغات اللي تدعمونها لتنوّع المرضى؟',
        a: 'يدعم النظام أكثر من ١٠٠ لغة بالدقة والأمان نفسهما. والكشف التلقائي للغة يعني أن المريض يتواصل بلغته المفضّلة بلا طلب مترجم، ولا انتظار.',
      },
    ],
  },
  'tour-operators': {
    en: [
      {
        q: 'Does it connect to my booking software?',
        a: 'Yes. Open integrates with leading tours-and-activities platforms via their APIs, reading live availability, rebooking reservations, and updating records automatically.',
      },
      {
        q: 'How does it handle weather cancellations?',
        a: 'It notifies guests proactively, offers alternative dates, and processes refunds or rebookings per your policy, clearing the “is it still running?” surge in seconds.',
      },
      {
        q: 'Can it really answer in my guests’ languages?',
        a: 'Yes, over 100 languages with automatic detection, with fluent, accurate replies about meeting points, gear, and timing.',
      },
      {
        q: 'What happens during peak season?',
        a: 'Open scales instantly. Response time stays under ten seconds no matter the volume, no seasonal hiring, no degraded service.',
      },
      {
        q: 'Does it work across all our channels?',
        a: 'Email, live chat, web forms, WhatsApp, social DMs, and more, Open meets guests wherever they reach out, in one place for your team.',
      },
      {
        q: 'What about the questions it shouldn’t answer alone?',
        a: 'You set the boundaries. Anything sensitive or unusual is escalated to your team with full conversation and booking context attached.',
      },
    ],
    ar: [
      {
        q: 'هل يتصل ببرنامج الحجز عندي؟',
        a: 'نعم. Open يتكامل مع أبرز منصات حجز الجولات والأنشطة عبر الـ APIs، يقرأ التوفّر المباشر، يعيد الحجوزات، ويحدّث السجلات تلقائياً.',
      },
      {
        q: 'كيف يتعامل مع إلغاءات الطقس؟',
        a: 'يبلّغ الضيوف من طرفه، يعرض تواريخ بديلة، ويعالج الاسترداد أو إعادة الحجز حسب سياستك، ويصفّي موجة «لا زالت قائمة؟» في ثوانٍ.',
      },
      {
        q: 'يجاوب فعلاً بلغات ضيوفي؟',
        a: 'نعم، أكثر من ١٠٠ لغة مع كشف تلقائي، ورد سليم وواضح عن نقاط اللقاء والمعدات والتوقيت.',
      },
      {
        q: 'وش يحدث في موسم الذروة؟',
        a: 'Open يتوسّع فوراً. يظل وقت الرد أقل من ١٠ ثوانٍ مهما زاد الحجم، بدون توظيف موسمي وبدون تدهور في الخدمة.',
      },
      {
        q: 'يعمل على كل قنواتنا؟',
        a: 'الإيميل والدردشة المباشرة ونماذج الويب وWhatsApp ورسائل السوشيال وغيرها، Open يقابل الضيوف وين ما تواصلوا، في مكان واحد لفريقك.',
      },
      {
        q: 'وش عن الأسئلة اللي ما المفروض يجاوبها لحاله؟',
        a: 'أنت اللي تحدّد الحدود. أي شيء حسّاس أو مو اعتيادي يتحوّل لفريقك بكامل المحادثة وسياق الحجز.',
      },
    ],
  },
  'travel-hospitality': {
    en: [
      {
        q: 'Can it handle last-minute booking changes?',
        a: 'Yes. Agent 5 checks availability in your booking system, processes changes based on your policies (same-day fees, availability, etc.), and sends updated confirmations. Works 24/7, even when your team is offline.',
      },
      {
        q: 'How does it handle weather cancellations for outdoor activities?',
        a: 'Agent 5 can monitor weather data and proactively notify customers of cancellations, offer rebooking options for alternative dates, or process refunds according to your policies. Reduces no-shows and customer frustration.',
      },
      {
        q: 'What about equipment questions for RV or camper rentals?',
        a: 'Agent 5 provides troubleshooting guides, equipment manuals, and step-by-step instructions for common issues (connecting hookups, operating appliances, etc.). When customers are on the road, they get help immediately instead of searching manuals.',
      },
      {
        q: 'Can it coordinate group bookings?',
        a: 'Yes. Agent 5 handles inquiries from multiple people booking together, coordinates timing, manages capacity across multiple slots, and keeps everyone informed. No more 20-person email threads.',
      },
      {
        q: 'How does refund processing work?',
        a: 'You set the rules (cancel 48hrs before = full refund, within 24hrs = 50%, etc.) and Agent 5 applies them automatically. Clear cases get processed instantly. Edge cases escalate to your team with full context.',
      },
      {
        q: 'What happens during peak season when volume spikes?',
        a: 'Agent 5 scales instantly. Summer rush? Holiday bookings? Bad weather affecting hundreds of reservations? Response time stays under 10 seconds regardless of volume. No hiring seasonal staff, no degraded service during your busiest weeks.',
      },
      {
        q: 'Does it work with FareHarbor and other booking platforms?',
        a: 'Yes. Agent 5 integrates with FareHarbor, Checkfront, Bokun, and other tour/activity booking systems through APIs. It reads availability, processes changes, and updates bookings automatically.',
      },
      {
        q: 'What about international customers and language barriers?',
        a: 'Agent 5 supports 100+ languages with automatic detection. Tourists from anywhere get help in their native language—no translation delays, no misunderstandings about important details.',
      },
    ],
    ar: [
      {
        q: 'هل يتعامل مع تغييرات الحجز في آخر لحظة؟',
        a: 'نعم. يفحص التوفّر في نظام الحجز عندك، وينفّذ التغيير حسب سياستك (رسوم نفس اليوم، التوفّر، وغيرها)، ويرسل التأكيد المحدّث. يشتغل ٢٤/٧، حتى لمّا يكون فريقك خارج الدوام.',
      },
      {
        q: 'كيف يتعامل مع إلغاءات الطقس للأنشطة الخارجية؟',
        a: 'يقدر النظام يراقب بيانات الطقس، فيبلّغ العملاء بالإلغاء قبل ما يسألون، ويعرض تواريخ بديلة، أو يعالج الاسترداد حسب سياستك. فتقلّ حالات عدم الحضور، ويقلّ الإحباط معها.',
      },
      {
        q: 'وش عن أسئلة معدات تأجير الكرفانات؟',
        a: 'يوفّر النظام أدلة حل المشكلات وكتيّبات المعدات وتعليمات خطوة بخطوة للحالات الشائعة (توصيل الوصلات، تشغيل الأجهزة، وغيرها). فيوصل الجواب للعميل وهو على الطريق فوراً، بدل تقليب الكتيّبات.',
      },
      {
        q: 'هل ينسّق حجوزات المجموعات؟',
        a: 'نعم. يرد على كل فرد في المجموعة، وينسّق التوقيت، ويوزّع السعة على أكثر من فترة، ويبقي الجميع على اطّلاع. لا مزيد من سلاسل إيميل فيها ٢٠ شخصاً.',
      },
      {
        q: 'كيف تعمل معالجة الاسترداد؟',
        a: 'أنت اللي تحدّد القاعدة — إلغاء قبل ٤٨ ساعة = استرداد كامل، خلال ٢٤ ساعة = ٥٠٪، وهكذا — ويطبّقها النظام تلقائياً. والحالات الواضحة تنحل فوراً، والاستثنائية يرفعها لفريقك بسياق كامل.',
      },
      {
        q: 'وش يصير في موسم الذروة لمّا يزيد الضغط؟',
        a: 'يتوسّع النظام فوراً. زحام الصيف؟ حجوزات العطلات؟ طقس سيّئ يضرب مئات الحجوزات في يوم واحد؟ يبقى وقت الاستجابة أقل من ١٠ ثوانٍ مهما كان الحجم. بلا توظيف موسمي، ولا خدمة تتراجع في أزحم أسابيعك.',
      },
      {
        q: 'هل يشتغل مع FareHarbor وغيرها من منصات الحجز؟',
        a: 'نعم. يتكامل مع FareHarbor، Checkfront، Bokun وغيرها من أنظمة حجز الجولات والأنشطة، عبر واجهاتها البرمجية. يقرأ التوفّر، وينفّذ التغييرات، ويحدّث الحجوزات تلقائياً.',
      },
      {
        q: 'وش عن العملاء الدوليين وحواجز اللغة؟',
        a: 'يدعم النظام أكثر من ١٠٠ لغة، ويتعرّف على اللغة تلقائياً. فيوصل الجواب للسائح من أي مكان بلغته الأم — بلا تأخير ترجمة، ولا سوء فهم في التفاصيل المهمة.',
      },
    ],
  },
};
